import CustomerChartsForm from "../statistics/CustomerChartsForm";
import ProjectCalendarSearchBar from "../ProjectCalendarSearchBar";
import {useEffect, useState} from "react";
import {getDefaultDateRange} from "../../libs/helper";
import {getKakaoUsersStatistics} from "../../api/statistics/statistics.api";
import {hasNoParticipants} from "../../libs/customerDistribution";
import SpecificCustomerStatistics from "../page/SpecificCustomerStatistics";
import {Dropdown} from "omoplay";
import {getEntryPointsToProject} from "../../api/project/project.api";
import InputGroup from "./InputGroup";

const GraphStatistics = ({periodStartDate, projectId, showFriendAddAndBlock}) => {
    const [dateRange, setDateRange] = useState(getDefaultDateRange())
    const [customers, setCustomers] = useState([])
    const [entryPoints, setEntryPoints] = useState([])
    const [entryPointId, setEntryPointId] = useState(undefined)
    /**
     * 이 탭의 **자체 로딩 표시**(2026-09-11 사용자 지시).
     *
     * 🔴 전역 오버레이만으로는 부족했다 — 이 조회는 참여 고객 전체를 훑어서 느리고
     *    (진입점 600개 고객사에서 5초 이상), 그 동안 차트 자리가 **빈 축만** 보여서
     *    "화면이 멈췄다" 로 읽혔다. 전역 오버레이는 다른 빠른 요청이 끝나면 꺼지기도 했다
     *    (`LoadingProvider` 의 boolean → 카운터 수정과 함께 본다).
     *
     * 🔑 **직전 데이터를 지우지 않는다.** 기간·진입점을 바꿀 때 화면을 비우면 매번 깜빡이므로,
     *    옛 그래프를 흐리게 남기고 위에 "불러오는 중" 을 얹는다(무엇을 기다리는지 보인다).
     */
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        let alive = true
        setIsLoading(true)
        getKakaoUsersStatistics(dateRange?.startDate, dateRange?.endDate, projectId, entryPointId)
            .then(({data}) => {
                // 🔑 응답이 늦게 도착한 옛 요청이 새 조건의 결과를 덮어쓰지 않게 한다
                //    (기간을 빠르게 두 번 바꾸면 실제로 일어난다).
                if (alive) setCustomers(data)
            })
            .finally(() => {
                if (alive) setIsLoading(false)
            })
        return () => { alive = false }
    }, [dateRange, projectId, entryPointId])

    useEffect(() => {
        if (projectId) {
            getEntryPointsToProject(projectId).then(({data}) => {
                setEntryPoints([{name: "전체"}, ...data.entryPoints])
            })
        }
    }, [projectId])

    return (
        <InputGroup
            label="고객 전환 통계"
            description="기간별 고객 전환 통계를 확인하실 수 있습니다"
        >
            <div className="flex-col gap-4">
                <div className="w-full flex-col gap-2">
                    <div>
                        {
                            entryPoints.length > 0 ?
                                <Dropdown variants="default"
                                          style={{width: "200px"}}
                                          titleStyle={{backgroundColor: "white"}}
                                          items={entryPoints.map(spot => ({label: spot.name, value: spot.id}))}
                                          onChanged={(items) => {
                                              setEntryPointId(items[0].value)
                                          }} /> : <></>
                        }
                    </div>
                    <div>
                        <ProjectCalendarSearchBar
                            initDateType="week"
                            periodStartDate={periodStartDate}
                            onConfirm={(dateProps) => {
                                setDateRange(dateProps)
                            }} />
                    </div>
                </div>
                {/*
                  로딩 표시. 차트를 감싸 놓고 위에 얹는다 — 자리를 차지하지 않으므로
                  로딩 전후로 화면이 위아래로 튀지 않는다.
                */}
                <div className="relative">
                    {isLoading && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
                            <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow border border-gray-200">
                                <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
                                <span className="text-sm text-gray-600">고객 통계를 불러오는 중입니다…</span>
                            </div>
                        </div>
                    )}

                    {/*
                      🔴 **빈 축 대신 말로 알려 준다**(2026-09-11 사용자 지시).
                         기본 기간이 '최근 일주일' 이라 **이미 끝난 프로젝트**를 열면 모든 그래프가
                         빈 축이 된다. 그때 화면이 "데이터가 없는 프로젝트" 처럼 보이는데
                         실제로는 **기간을 잘못 보고 있는 것**이다.

                      🔑 로딩 중에는 띄우지 않는다(그 자리는 스피너가 쓴다) — 아직 응답을 못 받은
                         상태와 "정말 없는" 상태는 다르다.
                    */}
                    {!isLoading && hasNoParticipants(customers) ? (
                        <div className="rounded border border-gray-200 bg-gray-50 py-16 text-center">
                            <p className="text-sm font-semibold text-gray-700">해당 기간에는 데이터가 없습니다.</p>
                            <p className="mt-1 text-xs text-gray-500">
                                {dateRange?.startDate && dateRange?.endDate
                                    ? `${dateRange.startDate} ~ ${dateRange.endDate} 기간에 참여한 고객이 없습니다.`
                                    : "선택한 기간에 참여한 고객이 없습니다."}
                                <br />
                                기본 조회 기간은 <strong className="text-gray-600">최근 일주일</strong>입니다 —
                                위에서 <strong className="text-gray-600">‘전체’</strong>나 다른 기간을 골라 다시 확인해보세요.
                            </p>
                        </div>
                    ) : (
                        /*
                          🔑 `flex-col gap-4` 가 **여기에** 있어야 한다(2026-09-22 신고 복구).

                          종전에는 `CustomerChartsForm`('신규 고객 수' 카드+차트)와
                          `SpecificCustomerStatistics`(연령·성별·히트맵)가 위 `flex-col gap-4` 의
                          **직계 자식**이라 그 gap 이 둘 사이 간격을 만들었다.
                          2026-09-11 에 로딩 오버레이를 넣으며 두 컴포넌트를 이 div 로 감쌀는데,
                          이 div 에는 flex 가 없어서 **간격이 통째로 사라졌다** —
                          '신규 고객 수' 카드 아래에 다음 섹션이 붙어 버린다.

                          ⚠️ 프로젝트 상세 > 고객 분석 탭과 사이드바 '고객 > 고객 전환 통계'
                             (`AllSpots.js`) 가 같은 컴포넌트를 쓰므로 두 화면이 함께 고쳐진다.
                        */
                        <div className={`flex-col gap-4 ${isLoading ? "opacity-40 pointer-events-none" : ""}`}>
                            <CustomerChartsForm customers={customers} />
                            <SpecificCustomerStatistics customers={customers} showFriendAddAndBlock={showFriendAddAndBlock} />
                        </div>
                    )}
                </div>
            </div>
        </InputGroup>
    )
}

export default GraphStatistics